import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { darken } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { toLocalDatetimeString, CONSISTENCY_OPTIONS } from "./trackerHelpers";

const Field = ({ label, children }) => (
  <Box sx={{ mb: 2 }}>
    <Typography
      variant="body2"
      sx={{ mb: 0.5 }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const EditEntryDialog = ({
  open,
  editingEntry,
  editingValue,
  setEditingValue,
  onSave,
  onDelete,
  onCancel,
  editingImageFile,
  setEditingImageFile,
  editingImagePreview,
  setEditingImagePreview,
}) => {
  const { t } = useTranslation();
  const update = (fields) => setEditingValue({ ...editingValue, ...fields });

  return (
    <Dialog
      open={open}
      onClose={onCancel}
    >
      <DialogTitle>{t("edit.title", { type: editingEntry?.type })}</DialogTitle>
      <DialogContent dividers>
        {editingEntry &&
          (editingEntry.type === "Diaper" ? (
            <>
              <Field label={t("edit.datetime")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.datetime
                      ? toLocalDatetimeString(editingValue.datetime)
                      : ""
                  }
                  onChange={(e) => update({ datetime: e.target.value })}
                  fullWidth
                />
              </Field>
              <Field label={t("edit.poop")}>
                <ToggleButtonGroup
                  value={editingValue.poop || "none"}
                  exclusive
                  onChange={(_, v) =>
                    v && update({ poop: v, konsistens: "normal" })
                  }
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="none">{t("common.no")}</ToggleButton>
                  <ToggleButton value="poop">{t("common.yes")}</ToggleButton>
                </ToggleButtonGroup>
              </Field>
              {editingValue.poop === "poop" && (
                <Field label={t("edit.consistency")}>
                  <ToggleButtonGroup
                    value={editingValue.konsistens || "normal"}
                    exclusive
                    onChange={(_, v) => v && update({ konsistens: v })}
                    fullWidth
                    size="small"
                  >
                    {CONSISTENCY_OPTIONS.map((opt) => (
                      <ToggleButton
                        key={opt.value}
                        value={opt.value}
                      >
                        {opt.label}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Field>
              )}
              {editingValue.poop === "poop" && (
                <Field label={t("edit.color")}>
                  <ToggleButtonGroup
                    value={editingValue.color || ""}
                    exclusive
                    onChange={(_, v) => v && update({ color: v })}
                    fullWidth
                    size="small"
                    sx={{ overflow: "visible" }}
                  >
                    {[
                      { value: "#FFF59D" },
                      { value: "#8D6E63" },
                      { value: "#AED581" },
                      { value: "#C1DABF" },
                    ].map((c) => (
                      <ToggleButton
                        key={c.value}
                        value={c.value}
                        sx={{
                          backgroundColor: c.value,
                          "&&.Mui-selected": {
                            backgroundColor: c.value,
                            boxShadow: "inset 0 0 0 3px #8fb9a8",
                          },
                          "&&.Mui-selected:hover": {
                            backgroundColor: c.value,
                          },
                        }}
                      ></ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Field>
              )}
              <Field label={t("edit.pee")}>
                <ToggleButtonGroup
                  value={editingValue.pee || "none"}
                  exclusive
                  onChange={(_, v) => v && update({ pee: v })}
                  fullWidth
                  size="small"
                >
                  <ToggleButton value="none">{t("common.no")}</ToggleButton>
                  <ToggleButton value="pee">{t("common.yes")}</ToggleButton>
                </ToggleButtonGroup>
              </Field>
              <Field label={t("edit.photo")}>
                <input
                  type="file"
                  accept="image/*"
                  id="diaper-image-upload"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setEditingImageFile(file);
                      setEditingImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
                {!editingImagePreview ? (
                  <label htmlFor="diaper-image-upload">
                    <Button variant="outlined" component="span" fullWidth size="small">
                      {t("edit.choose_image")}
                    </Button>
                  </label>
                ) : (
                  <Box>
                    <label htmlFor="diaper-image-upload">
                      <img
                        src={editingImagePreview}
                        alt="Preview"
                        style={{ maxHeight: 100, borderRadius: 8, cursor: "pointer" }}
                      />
                    </label>
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      onClick={() => {
                        setEditingImageFile(null);
                        setEditingImagePreview(null);
                      }}
                      fullWidth
                    >
                      {t("edit.remove_image")}
                    </Button>
                  </Box>
                )}
              </Field>
            </>
          ) : editingEntry.type === "Feeding" ? (
            <>
              <Field label={t("edit.start_time")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.start
                      ? toLocalDatetimeString(editingValue.start)
                      : ""
                  }
                  onChange={(e) => update({ start: e.target.value })}
                  fullWidth
                />
              </Field>
              {!editingEntry.active && (
                <Field label={t("edit.end_time")}>
                  <TextField
                    type="datetime-local"
                    value={
                      editingValue.end
                        ? toLocalDatetimeString(editingValue.end)
                        : ""
                    }
                    onChange={(e) => update({ end: e.target.value })}
                    fullWidth
                  />
                </Field>
              )}
              <Field label={t("edit.breast")}>
                <Select
                  value={editingValue.side || ""}
                  onChange={(e) => update({ side: e.target.value })}
                  fullWidth
                  size="small"
                >
                  <MenuItem value="none">{t("edit.none")}</MenuItem>
                  <MenuItem value="left">{t("edit.left")}</MenuItem>
                  <MenuItem value="right">{t("edit.right")}</MenuItem>
                  <MenuItem value="both">{t("edit.both")}</MenuItem>
                </Select>
              </Field>
              <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    MM (ml)
                  </Typography>
                  <TextField
                    type="number"
                    value={editingValue.extraMilkMM}
                    onChange={(e) => update({ extraMilkMM: e.target.value })}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 0.5 }}
                  >
                    MME (ml)
                  </Typography>
                  <TextField
                    type="number"
                    value={editingValue.extraMilkMME}
                    onChange={(e) => update({ extraMilkMME: e.target.value })}
                    fullWidth
                  />
                </Box>
              </Box>
              <Field label={t("edit.solid_food_note")}>
                <TextField
                  multiline
                  minRows={1}
                  value={editingValue.solidFoodNote}
                  onChange={(e) => update({ solidFoodNote: e.target.value })}
                  fullWidth
                />
              </Field>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingValue.dVitamin || false}
                      onChange={(e) => update({ dVitamin: e.target.checked })}
                      color="primary"
                    />
                  }
                  label="💊 D-drops"
                />
              </Box>
              <Field label={t("edit.photo")}>
                <input
                  type="file"
                  accept="image/*"
                  id="feeding-image-upload"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setEditingImageFile(file);
                      setEditingImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
                {!editingImagePreview ? (
                  <label htmlFor="feeding-image-upload">
                    <Button variant="outlined" component="span" fullWidth size="small">
                      {t("edit.choose_image")}
                    </Button>
                  </label>
                ) : (
                  <Box>
                    <label htmlFor="feeding-image-upload">
                      <img
                        src={editingImagePreview}
                        alt="Preview"
                        style={{ maxHeight: 100, borderRadius: 8, cursor: "pointer" }}
                      />
                    </label>
                    <Button
                      size="small"
                      color="error"
                      variant="text"
                      onClick={() => {
                        setEditingImageFile(null);
                        setEditingImagePreview(null);
                      }}
                      fullWidth
                    >
                      {t("edit.remove_image")}
                    </Button>
                  </Box>
                )}
              </Field>
            </>
          ) : editingEntry.type === "Extracted Milk" ? (
            <>
              <Field label={t("edit.datetime")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.datetime
                      ? toLocalDatetimeString(editingValue.datetime)
                      : ""
                  }
                  onChange={(e) => update({ datetime: e.target.value })}
                  fullWidth
                />
              </Field>
              <Field label={t("edit.extracted_milk_ml")}>
                <TextField
                  type="number"
                  value={editingValue.amount || ""}
                  onChange={(e) => update({ amount: e.target.value })}
                  fullWidth
                />
              </Field>
            </>
          ) : editingEntry.type === "Weight" ? (
            <>
              <Field label={t("edit.datetime")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.datetime
                      ? toLocalDatetimeString(editingValue.datetime)
                      : ""
                  }
                  onChange={(e) => update({ datetime: e.target.value })}
                  fullWidth
                />
              </Field>
              <Field label={t("edit.weight_g")}>
                <TextField
                  type="number"
                  value={editingValue.weight || ""}
                  onChange={(e) => update({ weight: e.target.value })}
                  fullWidth
                />
              </Field>
            </>
          ) : editingEntry.type === "Feeding Plan" ? (
            <>
              <Field label={t("edit.scheduled_time")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.datetime
                      ? toLocalDatetimeString(editingValue.datetime)
                      : ""
                  }
                  onChange={(e) => update({ datetime: e.target.value })}
                  fullWidth
                />
              </Field>
              <Field label={t("edit.extra_milk_mm")}>
                <TextField
                  type="number"
                  value={editingValue.extraMilkMM || ""}
                  onChange={(e) => update({ extraMilkMM: e.target.value })}
                  fullWidth
                />
              </Field>
              <Field label={t("edit.extra_milk_mme")}>
                <TextField
                  type="number"
                  value={editingValue.extraMilkMME || ""}
                  onChange={(e) => update({ extraMilkMME: e.target.value })}
                  fullWidth
                />
              </Field>
              <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ mr: 1 }}
                >
                  {t("edit.completed")}
                </Typography>
                <Checkbox
                  checked={editingValue.done || false}
                  onChange={(e) => update({ done: e.target.checked })}
                  color="primary"
                />
              </Box>
            </>
          ) : editingEntry.type === "Tummy Time" ? (
            <>
              <Field label={t("edit.start_time")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.start
                      ? toLocalDatetimeString(editingValue.start)
                      : ""
                  }
                  onChange={(e) => update({ start: e.target.value })}
                  fullWidth
                />
              </Field>
              {!editingEntry.active && (
                <Field label={t("edit.end_time")}>
                  <TextField
                    type="datetime-local"
                    value={
                      editingValue.end
                        ? toLocalDatetimeString(editingValue.end)
                        : ""
                    }
                    onChange={(e) => update({ end: e.target.value })}
                    fullWidth
                  />
                </Field>
              )}
            </>
          ) : (
            // Sleep (default fallback)
            <>
              <Field label={t("edit.start_time")}>
                <TextField
                  type="datetime-local"
                  value={
                    editingValue.start
                      ? toLocalDatetimeString(editingValue.start)
                      : ""
                  }
                  onChange={(e) => update({ start: e.target.value })}
                  fullWidth
                />
              </Field>
              {!editingEntry.active && (
                <Field label={t("edit.end_time")}>
                  <TextField
                    type="datetime-local"
                    value={
                      editingValue.end
                        ? toLocalDatetimeString(editingValue.end)
                        : ""
                    }
                    onChange={(e) => update({ end: e.target.value })}
                    fullWidth
                  />
                </Field>
              )}
              <Field label={t("edit.head_position")}>
                <Select
                  value={editingValue.headPosition || "middle"}
                  onChange={(e) => update({ headPosition: e.target.value })}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="left">{t("edit.left")}</MenuItem>
                  <MenuItem value="middle">{t("edit.middle")}</MenuItem>
                  <MenuItem value="right">{t("edit.right")}</MenuItem>
                </Select>
              </Field>
            </>
          ))}
      </DialogContent>
      <DialogActions sx={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          onClick={onDelete}
          color="error"
        >
          {t("common.delete")}
        </Button>
        <Box>
          <Button onClick={onCancel}>{t("common.cancel")}</Button>
          <Button
            onClick={onSave}
            variant="contained"
            sx={{
              backgroundColor: darken("#7AA490", 0.2),
              "&:hover": { backgroundColor: darken("#7AA490", 0.3) },
              ml: 1,
            }}
          >
            {t("common.save")}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditEntryDialog;
